import { useCallback } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import { BilateralTradeCsvLanguage } from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_service_pb'
import { useTranslation } from 'react-i18next'

import { DownloadModal } from '~/pages/AdminUploads/components/DownloadModal'
import { useExportBilateralTrades } from '~/pages/AdminUploads/hooks/useExportBilateralTrades'
import type { BilateralTradeCsvExportRow } from '~/pages/AdminUploads/models/bilateralTradeCsvExport'
import {
  bilateralTradeCsvExportFileName,
  buildBilateralTradeCsvExport,
  toBilateralTradeCsvExportRows,
} from '~/pages/AdminUploads/models/bilateralTradeCsvExport'

type Props = {
  isOpen: boolean
  onClose: () => void
}

/**
 * 入稿済み相対取引契約のダウンロード確認モーダル
 * (uploads/common.md §6 / AC-DUC-028 / AC-DUC-032)。
 */
export const BilateralTradeDownloadModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()
  // 英語表示ではファイル名・ヘッダー行・区分値を英語で出す (common.md §6)。区分値のセルは
  // backend が request の言語で返すので、表示言語をそのまま渡す。
  const isEnglish = i18n.language.startsWith('en')
  const { mutateAsync: exportAsync } = useExportBilateralTrades()
  const typeName = t('page:adminUploads.types.bilateralTrade.name')

  const exportRows = useCallback(async () => {
    const resp = await exportAsync({
      language: isEnglish
        ? BilateralTradeCsvLanguage.ENGLISH
        : BilateralTradeCsvLanguage.JAPANESE,
    })
    return {
      rows: toBilateralTradeCsvExportRows(resp.rows),
      dataUpdatedAt:
        resp.dataUpdatedAt !== undefined
          ? timestampDate(resp.dataUpdatedAt)
          : undefined,
    }
  }, [exportAsync, isEnglish])

  const buildCsv = useCallback(
    (rows: BilateralTradeCsvExportRow[]) =>
      buildBilateralTradeCsvExport(rows, isEnglish),
    [isEnglish],
  )

  const fileName = useCallback(
    () => bilateralTradeCsvExportFileName(isEnglish),
    [isEnglish],
  )

  return (
    <DownloadModal<BilateralTradeCsvExportRow>
      buildCsv={buildCsv}
      exportRows={exportRows}
      fileName={fileName}
      isOpen={isOpen}
      onClose={onClose}
      typeName={typeName}
    />
  )
}
